// Simple hash-based router implementation
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface RouterContextType {
  pathname: string;
  navigate: (to: string) => void;
}

const RouterContext = createContext<RouterContextType>({
  pathname: '/',
  navigate: () => {},
});

export const HashRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pathname, setPathname] = useState(() => {
    const hash = window.location.hash.slice(1) || '/';
    return hash;
  });

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) || '/';
      setPathname(hash);
      window.scrollTo(0, 0);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = useCallback((to: string) => {
    window.location.hash = to;
  }, []);

  return (
    <RouterContext.Provider value={{ pathname, navigate }}>
      {children}
    </RouterContext.Provider>
  );
};

export const useLocation = () => {
  const { pathname } = useContext(RouterContext);
  return { pathname };
};

export const useNavigate = () => {
  const { navigate } = useContext(RouterContext);
  return navigate;
};

interface LinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

export const Link: React.FC<LinkProps> = ({ to, children, className, onClick }) => {
  const { navigate } = useContext(RouterContext);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to);
    onClick?.();
  };

  return (
    <a href={`#${to}`} className={className} onClick={handleClick}>
      {children}
    </a>
  );
};

interface RouteChildProps {
  path: string;
  element: React.ReactNode;
}

interface RoutesProps {
  children: React.ReactNode;
}

export const Routes: React.FC<RoutesProps> = ({ children }) => {
  const { pathname } = useContext(RouterContext);
  
  let matchedElement: React.ReactNode = null;
  let bestMatchLength = 0;
  
  // First pass: find the best match (longest matching path)
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      const props = child.props as RouteChildProps;
      const { path, element } = props;
      
      if (path === '*') {
        // Wildcard is lowest priority, only use if no other match
        if (!matchedElement) {
          matchedElement = element;
        }
      } else if (path === pathname) {
        // Exact match is highest priority
        matchedElement = element;
        bestMatchLength = path.length;
      } else if (path && pathname.startsWith(path) && path !== '/') {
        // Partial match: only use if this path is longer than current best match
        // This ensures /products/handheld/des-c136 matches before /products/handheld
        const isMoreSpecific = path.length > bestMatchLength;
        // Also ensure we're matching complete path segments (avoid matching /products/handheld-x with /products/handheld)
        const nextChar = pathname[path.length];
        const isCompleteSegment = nextChar === '/' || nextChar === undefined;
        
        if (isMoreSpecific && isCompleteSegment) {
          matchedElement = element;
          bestMatchLength = path.length;
        }
      }
    }
  });
  
  // Default to home if no match
  if (!matchedElement && pathname === '/') {
    React.Children.forEach(children, (child) => {
      if (!matchedElement && React.isValidElement(child)) {
        const props = child.props as RouteChildProps;
        if (props.path === '/') {
          matchedElement = props.element;
        }
      }
    });
  }
  
  // Fallback to 404
  if (!matchedElement) {
    React.Children.forEach(children, (child) => {
      if (!matchedElement && React.isValidElement(child)) {
        const props = child.props as RouteChildProps;
        if (props.path === '*') {
          matchedElement = props.element;
        }
      }
    });
  }
  
  return <>{matchedElement}</>;
};

interface RouteProps {
  path: string;
  element: React.ReactNode;
}

export const Route: React.FC<RouteProps> = () => null;

// Simple hook for route params
interface RouteParams {
  [key: string]: string;
}

export const useParams = (): RouteParams => {
  const { pathname } = useContext(RouterContext);
  const params: RouteParams = {};
  
  // Simple param extraction from pathname
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length >= 2) {
    // Extract product ID from /products/category/product-id pattern
    const lastPart = parts[parts.length - 1];
    if (lastPart) {
      params.productId = lastPart;
    }
    // Extract app ID from /applications/app-id pattern
    if (parts[0] === 'applications' && parts[1]) {
      params.appId = parts[1];
    }
  }
  
  return params;
};
